import { createRouter as createTanStackRouter } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import type { SupabaseClient } from "@supabase/supabase-js";
import { routeTree } from "./routeTree.gen";
import { queryClient } from "./lib/queryClient";
import { supabase } from "./integrations/supabase/client";

/**
 * Router context — passed to every route's beforeLoad/loader.
 * Follows the TanStack Start Phase 3 migration pattern:
 *   - queryClient: shared TanStack Query cache, avoids re-creating per-route
 *   - supabase: browser Supabase client (undefined in SSR — check before use)
 */
export interface RouterContext {
  queryClient: QueryClient;
  supabase: SupabaseClient | undefined;
}

/**
 * Extended router context after beforeLoad returns session.
 * Used by Phase 4 loaders to access authenticated user data.
 */
export interface RouterContextWithSession extends RouterContext {
  session?: {
    user: { id: string };
    access_token: string;
  };
}

export function getRouter() {
  const router = createTanStackRouter({
    routeTree,
    scrollRestoration: true,
    defaultPreload: "intent",
    context: {
      queryClient,
      supabase,
    } satisfies RouterContext,
  });

  return router;
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof getRouter>;
  }
}
